// Size guide auto-snap. Lives inside the first product photo frame ([data-size-guide-media]).
// The first time the size guide is switched on, it samples a small CORS copy of the photo,
// separates the garment from the background, and moves the [data-measure] rulers:
//   chest  - horizontal, across the torso a little below the armpits (side seam to side seam)
//   length - vertical, high shoulder point to hem, just outside the torso's right edge
//   sleeve - angled, from the left shoulder point to the far end of the left cuff, so it follows
//            sleeves that hang straight down or spread out diagonally; short sleeves (tees) measure
//            from the shoulder seam to the middle of the cuff opening
// Background: each row's colour comes from its left and right edges (tolerates wall/floor
// gradients), and the garment threshold adapts to how noisy those edges are, so pale garments on
// white still separate. Positions are mapped through object-contain letterboxing into the frame.
// If the photo doesn't read as one garment, the rulers keep their default spots
// (data-snap="fallback"). Results are re-applied on size-guide partial refreshes and resizes.

const SAMPLE_WIDTH = 240;
const EDGE_BAND = 0.04; // fraction of width sampled on each side for the row background
const ROW_MIN = 0.03; // a row needs this fraction of garment pixels to count
const STRIPE_GAP = 0.09; // fraction of height: column gaps up to this are bridged (stripes)

class SSizeSnap extends HTMLElement {
  connectedCallback() {
    this.frame = this.closest('[data-size-guide-media]');
    this.toggle = document.getElementById('size-guide-toggle');
    if (!this.frame || !this.toggle) return;
    // Moved onto another photo (a colour switch): measure that photo afresh instead of reusing the old fit.
    const src = this.frame.querySelector('img')?.getAttribute('src') || '';
    if (this.sampledSrc !== undefined && this.sampledSrc !== src) {
      this.started = false;
      this.result = null;
      for (const el of this.frame.querySelectorAll('[data-measure], [data-measure] .measure-label')) {
        if (el.dataset.homeStyle !== undefined) el.setAttribute('style', el.dataset.homeStyle);
      }
    }
    this.sampledSrc = src;
    for (const el of this.frame.querySelectorAll('[data-measure], [data-measure] .measure-label')) {
      if (el.dataset.homeStyle === undefined) el.dataset.homeStyle = el.getAttribute('style') || '';
    }

    this.onToggle = () => {
      if (this.toggle.checked) this.run();
    };
    this.toggle.addEventListener('change', this.onToggle);
    if (this.toggle.checked) this.run();

    this.observer = new MutationObserver(() => this.apply());
    this.observer.observe(this.frame, { childList: true, subtree: true });
    this.resizeObserver = new ResizeObserver(() => this.apply());
    this.resizeObserver.observe(this.frame);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('change', this.onToggle);
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  async run() {
    if (this.started) return;
    this.started = true;
    const img = this.frame.querySelector('img');
    if (!img) return this.fallback();
    try {
      const sample = await this.loadSample(img);
      this.result = this.detect(sample);
      if (!this.result) return this.fallback();
      this.frame.dataset.snap = 'auto';
      this.apply();
    } catch {
      this.fallback();
    }
  }

  fallback() {
    this.result = null;
    this.frame.dataset.snap = 'fallback';
  }

  loadSample(img) {
    const url = new URL(img.currentSrc || img.src, window.location.href);
    url.searchParams.set('width', String(SAMPLE_WIDTH));
    return new Promise((resolve, reject) => {
      const sample = new Image();
      sample.crossOrigin = 'anonymous';
      sample.decoding = 'async';
      sample.onload = () => resolve(sample);
      sample.onerror = reject;
      sample.src = url.toString();
    });
  }

  buildMask(data, w, h) {
    const band = Math.max(2, Math.round(w * EDGE_BAND));
    const backgrounds = [];
    let noise = 0;
    for (let y = 0; y < h; y++) {
      const edges = [];
      for (let x = 0; x < band; x++) edges.push(this.pixel(data, w, x, y), this.pixel(data, w, w - 1 - x, y));
      const bg = this.median(edges);
      backgrounds.push(bg);
      noise += this.median(edges.map((p) => this.distance(p, bg)));
    }
    noise /= h;
    // A busy background (lifestyle shot) makes the edges disagree with each other.
    if (noise > 20) return null;
    const threshold = Math.min(48, Math.max(18, noise * 4 + 14));

    const mask = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = band; x < w - band; x++) {
        if (this.distance(this.pixel(data, w, x, y), backgrounds[y]) > threshold) mask[y * w + x] = 1;
      }
    }

    // Close small gaps (soft highlights on pale fabric), then drop speckle.
    const dilated = new Uint8Array(w * h);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        let any = 0;
        for (let dy = -1; dy <= 1 && !any; dy++) for (let dx = -1; dx <= 1; dx++) if (mask[(y + dy) * w + x + dx]) { any = 1; break; }
        dilated[y * w + x] = any;
      }
    }
    const clean = new Uint8Array(w * h);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        let all = 1;
        for (let dy = -1; dy <= 1 && all; dy++) for (let dx = -1; dx <= 1; dx++) if (!dilated[(y + dy) * w + x + dx]) { all = 0; break; }
        clean[y * w + x] = all;
      }
    }
    // Bridge short gaps down each column (white stripes between dark ones on a white photo), so a
    // striped shirt still reads as one solid shape. Gaps taller than STRIPE_GAP stay open.
    const maxGap = Math.round(h * STRIPE_GAP);
    for (let x = 0; x < w; x++) {
      let last = -1;
      for (let y = 0; y < h; y++) {
        if (!clean[y * w + x]) continue;
        if (last >= 0 && y - last > 1 && y - last <= maxGap) for (let k = last + 1; k < y; k++) clean[k * w + x] = 1;
        last = y;
      }
    }
    return clean;
  }

  // Returns ruler geometry in image fractions (0..1), or null when not confident.
  detect(sample) {
    const w = SAMPLE_WIDTH;
    const h = Math.max(1, Math.round((sample.naturalHeight / sample.naturalWidth) * w));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(sample, 0, 0, w, h);
    const mask = this.buildMask(ctx.getImageData(0, 0, w, h).data, w, h);
    if (!mask) return null;
    const on = (x, y) => x >= 0 && y >= 0 && x < w && y < h && mask[y * w + x] === 1;

    const rows = [];
    for (let y = 0; y < h; y++) {
      let left = -1;
      let right = -1;
      let count = 0;
      for (let x = 0; x < w; x++) {
        if (!on(x, y)) continue;
        count++;
        if (left < 0) left = x;
        right = x;
      }
      rows.push(count >= w * ROW_MIN ? { left, right, span: right - left } : null);
    }
    const garmentRows = rows.map((row, y) => (row ? y : -1)).filter((y) => y >= 0);
    if (garmentRows.length < h * 0.2) return null;
    const maxSpan = Math.max(...garmentRows.map((y) => rows[y].span));
    if (maxSpan < w * 0.25 || maxSpan > w * 0.98) return null;

    // Shoulder line: where the outline widens sharply past the hood/neck at the top.
    const topRows = garmentRows.slice(0, Math.max(3, Math.round(garmentRows.length * 0.08)));
    const headSpan = this.median(topRows.map((y) => rows[y].span));
    const shoulderY = garmentRows.find((y) => rows[y].span >= Math.max(headSpan * 1.6, maxSpan * 0.35));
    if (shoulderY == null) return null;

    // Torso centre from the shoulder row; hem = bottom of the centre column.
    const centerX = Math.round((rows[shoulderY].left + rows[shoulderY].right) / 2);
    let hemY = shoulderY;
    for (let y = shoulderY; y < h; y++) {
      if (on(centerX, y) || on(centerX - 1, y) || on(centerX + 1, y)) hemY = y;
      else if (y - hemY > h * 0.03) break;
    }
    const torsoLength = hemY - shoulderY;
    if (torsoLength < h * 0.15) return null;

    const run = (y) => {
      let l = centerX;
      let r = centerX;
      while (on(l - 1, y)) l--;
      while (on(r + 1, y)) r++;
      return { l, r, span: r - l };
    };

    // Torso width: the narrowest centre run in the lower body (arms that touch the body at chest
    // height usually separate by the hem). Then measure the chest a little below the armpits,
    // clamped to that torso width.
    let torso = null;
    for (let y = Math.round(hemY - torsoLength * 0.35); y <= hemY - torsoLength * 0.05; y++) {
      const r = run(y);
      if (!torso || r.span < torso.span) torso = r;
    }
    const chestY = Math.round(shoulderY + torsoLength * 0.32);
    let chest = run(chestY);
    if (chest.span > torso.span * 1.12) {
      const mid = (torso.l + torso.r) / 2;
      const half = (torso.span * 1.04) / 2;
      chest = { l: mid - half, r: mid + half, span: half * 2 };
    }

    // Sleeve: from the left shoulder point to the farthest garment pixel out past the torso on the
    // left (the cuff end), whatever direction the sleeve points.
    const shoulderPoint = { x: Math.min(rows[shoulderY].left, chest.l), y: shoulderY };
    let cuff = null;
    let best = 0;
    for (let y = shoulderY; y <= Math.min(h - 1, hemY + torsoLength * 0.2); y++) {
      for (let x = 0; x < chest.l - w * 0.01; x++) {
        if (!on(x, y)) continue;
        const d = Math.hypot(x - shoulderPoint.x, y - shoulderPoint.y);
        if (d > best) {
          best = d;
          cuff = { x, y };
        }
      }
    }
    if (!cuff || best < torsoLength * 0.45) {
      // Short sleeves (tees): the long-sleeve search lands near the armpit. Measure instead from
      // where the left torso edge meets the top outline (shoulder seam) to the middle of the
      // sleeve's outermost edge (the cuff opening).
      const seamX = Math.round(torso.l);
      let seamY = -1;
      for (let y = 0; y < shoulderY + torsoLength * 0.2 && y < h; y++) {
        if (on(seamX, y)) {
          seamY = y;
          break;
        }
      }
      let minX = w;
      const limitY = Math.min(h - 1, Math.round(shoulderY + torsoLength * 0.6));
      for (let y = shoulderY; y <= limitY; y++) {
        for (let x = 0; x < seamX - w * 0.01; x++) {
          if (on(x, y)) {
            if (x < minX) minX = x;
            break;
          }
        }
      }
      const cuffRows = [];
      for (let y = shoulderY; y <= limitY; y++) if (on(minX, y) || on(minX + 1, y) || on(minX + 2, y)) cuffRows.push(y);
      if (seamY < 0 || !cuffRows.length) return null;
      const short = { x: minX, y: cuffRows[Math.floor(cuffRows.length / 2)] };
      const shortLength = Math.hypot(short.x - seamX, short.y - seamY);
      if (shortLength < torsoLength * 0.15) return null;
      shoulderPoint.x = seamX;
      shoulderPoint.y = seamY;
      cuff = short;
    }

    const gap = w * 0.02;
    return {
      ratio: w / h,
      chest: { x: chest.l / w, y: chestY / h, length: chest.span / w },
      length: { x: (torso.r + gap) / w, y: shoulderY / h, length: torsoLength / h },
      sleeve: { x1: shoulderPoint.x / w, y1: shoulderPoint.y / h, x2: cuff.x / w, y2: cuff.y / h },
    };
  }

  // Maps image fractions into the frame (object-contain) and moves the rulers.
  apply() {
    if (!this.result) return;
    const box = this.frame.getBoundingClientRect();
    if (!box.width || !box.height) return;
    const frameRatio = box.width / box.height;
    const { ratio } = this.result;
    const scaleX = ratio >= frameRatio ? 1 : ratio / frameRatio;
    const scaleY = ratio >= frameRatio ? frameRatio / ratio : 1;
    const offX = (1 - scaleX) / 2;
    const offY = (1 - scaleY) / 2;
    const fx = (value) => offX + value * scaleX;
    const fy = (value) => offY + value * scaleY;
    const pct = (value) => `${(value * 100).toFixed(2)}%`;

    for (const ruler of this.frame.querySelectorAll('[data-measure]')) {
      const kind = ruler.dataset.measure;
      const g = this.result[kind];
      if (!g) continue;
      if (kind === 'chest') {
        this.setStyle(ruler, { left: pct(fx(g.x)), top: pct(fy(g.y)), width: pct(g.length * scaleX) });
      } else if (kind === 'length') {
        this.setStyle(ruler, { left: pct(fx(g.x)), top: pct(fy(g.y)), height: pct(g.length * scaleY) });
      } else if (kind === 'sleeve') {
        // Angled ruler: anchor at the shoulder, length in px, rotate the line toward the cuff and
        // counter-rotate the label so it stays readable.
        const dx = (fx(g.x2) - fx(g.x1)) * box.width;
        const dy = (fy(g.y2) - fy(g.y1)) * box.height;
        const length = Math.hypot(dx, dy);
        const angle = (Math.atan2(-dx, dy) * 180) / Math.PI;
        this.setStyle(ruler, {
          left: pct(fx(g.x1)),
          top: pct(fy(g.y1)),
          height: `${length.toFixed(1)}px`,
          transformOrigin: '0 0',
          transform: `rotate(${angle.toFixed(2)}deg)`,
        });
        const label = ruler.querySelector('.measure-label');
        if (label) this.setStyle(label, { transform: `rotate(${(-angle).toFixed(2)}deg)` });
      }
    }
  }

  setStyle(element, values) {
    for (const [key, value] of Object.entries(values)) {
      if (element.style[key] !== value) element.style[key] = value;
    }
  }

  pixel(data, w, x, y) {
    const i = (y * w + x) * 4;
    return [data[i], data[i + 1], data[i + 2]];
  }

  distance(a, b) {
    return Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[2] - b[2]));
  }

  median(values) {
    if (!values.length) return 0;
    if (Array.isArray(values[0])) return [0, 1, 2].map((c) => this.median(values.map((v) => v[c])));
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }
}

if (!customElements.get('s-size-snap')) customElements.define('s-size-snap', SSizeSnap);
